/**
 * `/settings/accounts` — which Sage accounts count as operating expense. New income-statement
 * accounts arrive `unclassified` and are never added to OpEx until classified here. Viewing needs
 * `canConfigure` (admin, manager); classifying needs `canConfigureAccounting` (admin).
 */
import { type ChangeEvent, useCallback, useMemo, useState } from 'react'

import { errorMessage } from '@/api/client'
import type { AccountClassification, AccountSetting } from '@/api/types'
import { ICONS } from '@/components/icons/icons'
import { Badge, type Tone } from '@/components/ui/Badge'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { HowItWorks } from '@/components/ui/HowItWorks'
import { Notice } from '@/components/ui/Notice'
import { Panel } from '@/components/ui/Panel'
import { EmptyState, ErrorState, Skeleton } from '@/components/ui/StateMessage'
import { useMe } from '@/features/auth/MeContext'
import layout from '@/features/runway/components/Layout.module.css'
import { PageHead } from '@/features/runway/components/PageHead'
import { quarterIdLabel } from '@/features/runway/lib/periodText'
import { useRunwayModel } from '@/features/runway/RunwayContext'
import { formatDate, formatMoney, sumMoney } from '@/lib/format'

import styles from './Settings.module.css'
import { useAccountSettings, useClassifyAccounts } from './useSettings'

type Filter = AccountClassification | 'all'

const FILTERS: Filter[] = ['unclassified', 'opex', 'excluded', 'all']

const FILTER_LABEL: Record<Filter, string> = {
  unclassified: 'Unclassified',
  opex: 'OpEx',
  excluded: 'Excluded',
  all: 'All',
}

const CLASS_LABEL: Record<AccountClassification, string> = {
  opex: 'OpEx',
  excluded: 'Excluded',
  unclassified: 'Unclassified',
}

const CLASS_TONE: Record<AccountClassification, Tone> = { opex: 'good', excluded: 'neutral', unclassified: 'warn' }

/** What a classification does to the numbers (confirmation dialog). */
const CONSEQUENCE: Record<AccountClassification, string> = {
  opex: 'count as department operating expense on the runway, in every quarter, for everyone',
  excluded: 'never be counted as department expense (and stop being flagged)',
  unclassified: 'stop counting and be flagged on the overview until someone classifies them',
}

function AccountsHelp({ canEdit }: { canEdit: boolean }) {
  return (
    <HowItWorks
      storageKey="fpa.help.accounts"
      automatic={
        <>
          The chart of accounts and every posted amount come from Sage. A new income-statement account starts{' '}
          <b>unclassified</b>: its money is shown as a warning on the overview, never added to OpEx on its own.
        </>
      }
      here={
        <>
          Decide what counts: <b>OpEx</b> (department expense), <b>Excluded</b> (never counted, e.g. suspense or
          financing) or back to unclassified. It applies to every quarter.
        </>
      }
      who={
        canEdit ? (
          <>FPA admins (you). Each change is recorded with your name and date.</>
        ) : (
          <>Only FPA admins. You can review the list; ask an FPA admin to classify an account.</>
        )
      }
    />
  )
}

function classifiedBy(account: AccountSetting): string {
  if (!account.classified_by) return '—'
  if (account.classified_by === 'system:revenue (auto)') return 'Revenue (auto)'
  if (account.classified_by.startsWith('system:')) return 'Initial setup'
  return `${account.classified_by.split('@')[0]} · ${formatDate(account.classified_at)}`
}

function FilterTab({
  filter,
  active,
  count,
  onSelect,
}: {
  filter: Filter
  active: boolean
  count: number
  onSelect: (filter: Filter) => void
}) {
  const handleClick = useCallback(() => onSelect(filter), [onSelect, filter])
  return (
    <button
      type="button"
      className={`${styles.tab} ${active ? styles.tabActive : ''}`}
      aria-pressed={active}
      onClick={handleClick}
    >
      {FILTER_LABEL[filter]}
      <span className={styles.count}>{count}</span>
    </button>
  )
}

function AccountRow({
  account,
  selectable,
  selected,
  onToggle,
}: {
  account: AccountSetting
  selectable: boolean
  selected: boolean
  onToggle: (acct: string) => void
}) {
  const handleToggle = useCallback(() => onToggle(account.acct), [onToggle, account.acct])
  return (
    <tr>
      {selectable ? (
        <td className={styles.checkCell}>
          <input
            type="checkbox"
            checked={selected}
            onChange={handleToggle}
            aria-label={`Select ${account.acct} ${account.title}`}
          />
        </td>
      ) : null}
      <td className={styles.acct}>{account.acct}</td>
      <td>{account.title}</td>
      <td>
        <Badge tone={CLASS_TONE[account.classification]} small>
          {CLASS_LABEL[account.classification]}
        </Badge>
      </td>
      <td className={styles.num}>{account.counted_amount ? formatMoney(account.counted_amount) : '—'}</td>
      <td className={styles.meta}>{classifiedBy(account)}</td>
    </tr>
  )
}

export function AccountsSettingsPage() {
  const me = useMe()
  const { runway } = useRunwayModel()
  const allowed = me.permissions.canConfigure
  const canEdit = me.permissions.canConfigureAccounting
  const accounts = useAccountSettings(runway.quarter, allowed)
  // The account list is per quarter: the selected one, or the quarter of the as-of date.
  const quarterLabel = quarterIdLabel(runway.quarter)
  const classify = useClassifyAccounts()
  const [filter, setFilter] = useState<Filter>('unclassified')
  const [term, setTerm] = useState('')
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set())
  const [flash, setFlash] = useState<string | null>(null)
  const [pending, setPending] = useState<AccountClassification | null>(null)
  const [showQuiet, setShowQuiet] = useState(false)

  // Unclassified accounts with no posted amount in the counted departments this quarter are noise
  // for finance: hidden by default, so the tab shows exactly what the overview banner reports.
  const isQuiet = useCallback((a: AccountSetting) => a.classification === 'unclassified' && !a.counted_amount, [])
  const quietCount = useMemo(() => (accounts.data ?? []).filter(isQuiet).length, [accounts.data, isQuiet])

  const counts = useMemo(() => {
    const out: Record<Filter, number> = { unclassified: 0, opex: 0, excluded: 0, all: 0 }
    for (const a of accounts.data ?? []) {
      if (!isQuiet(a)) out[a.classification] += 1
      out.all += 1
    }
    return out
  }, [accounts.data, isQuiet])

  const rows = useMemo(() => {
    const needle = term.trim().toLowerCase()
    return (accounts.data ?? []).filter(
      (a) =>
        (filter === 'all' || a.classification === filter) &&
        (filter !== 'unclassified' || showQuiet || !isQuiet(a)) &&
        (!needle || a.acct.includes(needle) || a.title.toLowerCase().includes(needle)),
    )
  }, [accounts.data, filter, term, showQuiet, isQuiet])
  const toggleQuiet = useCallback(() => setShowQuiet((v) => !v), [])

  const visibleSelected = rows.filter((a) => selected.has(a.acct)).map((a) => a.acct)
  const allVisibleSelected = rows.length > 0 && visibleSelected.length === rows.length

  const handleFilter = useCallback((next: Filter) => {
    setFilter(next)
    setSelected(new Set())
  }, [])
  const handleSearch = useCallback((event: ChangeEvent<HTMLInputElement>) => setTerm(event.target.value), [])
  const handleToggle = useCallback((acct: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(acct)) next.delete(acct)
      else next.add(acct)
      return next
    })
  }, [])
  const handleToggleAll = useCallback(() => {
    setSelected(allVisibleSelected ? new Set() : new Set(rows.map((a) => a.acct)))
  }, [allVisibleSelected, rows])

  const { mutate, reset } = classify
  const selectedAmount = useMemo(
    () =>
      sumMoney(
        rows.filter((a) => selected.has(a.acct)),
        (a) => a.counted_amount,
      ),
    [rows, selected],
  )
  const confirmApply = useCallback(() => {
    const list = visibleSelected
    const classification = pending
    if (list.length === 0 || classification === null) return
    setFlash(null)
    mutate(
      { accounts: list, classification },
      {
        onSuccess: () => {
          setPending(null)
          setSelected(new Set())
          setFlash(
            `${list.length} ${list.length === 1 ? 'account' : 'accounts'} marked as ${CLASS_LABEL[classification]}. The runway now reflects it.`,
          )
        },
      },
    )
  }, [mutate, visibleSelected, pending])
  const cancelApply = useCallback(() => {
    reset()
    setPending(null)
  }, [reset])
  const markOpex = useCallback(() => setPending('opex'), [])
  const markExcluded = useCallback(() => setPending('excluded'), [])
  const markUnclassified = useCallback(() => setPending('unclassified'), [])
  const handleRetry = useCallback(() => {
    void accounts.refetch()
  }, [accounts])

  if (!allowed) {
    return (
      <div className={layout.view}>
        <PageHead title="Accounts" sub="Which Sage accounts count as operating expense." badge={null} />
        <ErrorState>
          Only FPA admins and managers can see account settings. Ask an FPA admin if an account looks miscounted.
        </ErrorState>
      </div>
    )
  }

  return (
    <div className={layout.view}>
      <PageHead
        title="Accounts"
        sub={`Which Sage accounts count as operating expense. Amounts are ${quarterLabel} posted actuals.`}
        badge={
          counts.unclassified > 0 ? (
            <Badge tone="warn">{counts.unclassified} unclassified</Badge>
          ) : (
            <Badge tone="good">All classified</Badge>
          )
        }
      />

      {flash ? (
        <div className={styles.flash} role="status">
          <ICONS.check size={14} className={styles.flashIcon} />
          {flash}
        </div>
      ) : null}
      {classify.isError && pending === null ? (
        <Notice tone="warn">{errorMessage(classify.error, 'Could not classify.')}</Notice>
      ) : null}
      <AccountsHelp canEdit={canEdit} />

      <Panel title="Income-statement accounts">
        <div className={styles.toolbar}>
          <div className={styles.tabs} role="group" aria-label="Filter by classification">
            {FILTERS.map((f) => (
              <FilterTab key={f} filter={f} active={filter === f} count={counts[f]} onSelect={handleFilter} />
            ))}
          </div>
          <label className={styles.search}>
            <ICONS.search size={13} />
            <input
              className={styles.searchInput}
              type="search"
              value={term}
              onChange={handleSearch}
              placeholder="Search number or title"
              aria-label="Search accounts"
            />
          </label>
        </div>

        {canEdit && visibleSelected.length > 0 ? (
          <div className={styles.bulk} role="group" aria-label="Bulk classification">
            <span className={styles.bulkLabel}>{visibleSelected.length} selected</span>
            <button type="button" className={styles.primary} onClick={markOpex} disabled={classify.isPending}>
              Mark as OpEx
            </button>
            <button type="button" className={styles.secondary} onClick={markExcluded} disabled={classify.isPending}>
              Mark as excluded
            </button>
            <button type="button" className={styles.secondary} onClick={markUnclassified} disabled={classify.isPending}>
              Back to unclassified
            </button>
          </div>
        ) : null}

        {accounts.isPending ? (
          <Skeleton label="Loading accounts" />
        ) : accounts.error ? (
          <ErrorState>
            {errorMessage(accounts.error, 'Could not load the accounts.')}{' '}
            <button type="button" className={styles.secondary} onClick={handleRetry}>
              <ICONS.refresh size={13} />
              Try again
            </button>
          </ErrorState>
        ) : null}
        {!accounts.isPending && !accounts.error && filter === 'unclassified' && quietCount > 0 ? (
          <div className={styles.quietToggle}>
            <button type="button" className={styles.linkButton} onClick={toggleQuiet} aria-pressed={showQuiet}>
              {showQuiet
                ? `Hide ${quietCount} without activity`
                : `Show ${quietCount} without activity in ${quarterLabel}`}
            </button>
          </div>
        ) : null}
        {accounts.isPending || accounts.error ? null : rows.length === 0 ? (
          <EmptyState>
            {filter === 'unclassified' && !term
              ? quietCount > 0 && !showQuiet
                ? `No unclassified account has activity in ${quarterLabel}: nothing is missing from the numbers.`
                : 'Every account is classified: nothing from Sage is waiting for a decision.'
              : term
                ? `No accounts match "${term.trim()}". Try a number or part of the title.`
                : `No ${FILTER_LABEL[filter]} accounts.`}
          </EmptyState>
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  {canEdit ? (
                    <th scope="col" className={styles.checkCell}>
                      <input
                        type="checkbox"
                        checked={allVisibleSelected}
                        onChange={handleToggleAll}
                        aria-label="Select all shown accounts"
                      />
                    </th>
                  ) : null}
                  <th scope="col">Account</th>
                  <th scope="col">Title</th>
                  <th scope="col">Classification</th>
                  <th scope="col" className={styles.num}>
                    {quarterLabel}
                  </th>
                  <th scope="col">Classified by</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((a) => (
                  <AccountRow
                    key={a.acct}
                    account={a}
                    selectable={canEdit}
                    selected={selected.has(a.acct)}
                    onToggle={handleToggle}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {pending !== null ? (
        <ConfirmDialog
          title={`Mark ${visibleSelected.length} ${visibleSelected.length === 1 ? 'account' : 'accounts'} as ${CLASS_LABEL[pending]}?`}
          confirmLabel={`Mark as ${CLASS_LABEL[pending]}`}
          tone={pending === 'opex' ? 'primary' : 'danger'}
          busy={classify.isPending}
          error={classify.isError ? errorMessage(classify.error, 'Could not classify.') : null}
          onConfirm={confirmApply}
          onCancel={cancelApply}
        >
          <p>
            Their actuals ({formatMoney(selectedAmount)} in {quarterLabel}) will {CONSEQUENCE[pending]}.
          </p>
          <p>The change is recorded with your name and can be reversed here.</p>
        </ConfirmDialog>
      ) : null}
    </div>
  )
}
